import { useState, useCallback, useRef, useEffect } from "react";
import { signIn, useSession } from "next-auth/react";
import { storeSessionForRedirect } from "@/libs/ga";
import { trackFunnel, storeAnalyticsIdentityForAuth } from "@/libs/analytics";

interface UseGooglePopupAuthReturn {
  isAuthenticating: boolean;
  isSuccess: boolean;
  error: string | null;
  initiateGoogleAuth: (callbackUrl?: string) => Promise<void>;
  resetAuth: () => void;
}

/**
 * Hook to handle Google OAuth in a popup window
 * - Opens Google auth in popup
 * - Polls for session changes
 * - Detects when auth completes
 * - Handles errors and cleanup
 */
export function useGooglePopupAuth(): UseGooglePopupAuthReturn {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cancelCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Latest session status for popup-close handler (interval closures would be stale). */
  const statusRef = useRef<"loading" | "authenticated" | "unauthenticated">("unauthenticated");
  const isSuccessRef = useRef(false);
  const { data: session, status } = useSession();
  const previousStatusRef = useRef(status);

  statusRef.current = status;

  // Detect session changes (unauthenticated → authenticated)
  useEffect(() => {
    if (
      isAuthenticating &&
      previousStatusRef.current !== 'authenticated' &&
      status === 'authenticated' &&
      session?.user
    ) {
      // Auth succeeded!
      isSuccessRef.current = true;
      setIsSuccess(true);
      setIsAuthenticating(false);
      cleanup();

      trackFunnel('acquisition', 'signin_completed', {
        auth_method: 'google',
        auth_flow: 'popup'
      });
    }
    previousStatusRef.current = status;
  }, [status, session, isAuthenticating]);

  // Cleanup function (optional: keep delayed "cancel vs success" check after popup closes)
  const cleanup = useCallback((opts?: { preservePostCloseCheck?: boolean }) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (!opts?.preservePostCloseCheck && cancelCheckTimeoutRef.current) {
      clearTimeout(cancelCheckTimeoutRef.current);
      cancelCheckTimeoutRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const initiateGoogleAuth = useCallback(async (callbackUrl: string = '/dashboard') => {
    try {
      setIsAuthenticating(true);
      setError(null);
      setIsSuccess(false);
      isSuccessRef.current = false;

      storeSessionForRedirect('oauth');
      storeAnalyticsIdentityForAuth();

      // Track auth initiation
      trackFunnel('acquisition', 'signin_started', {
        auth_method: 'google',
        auth_flow: 'popup',
        callback_url: callbackUrl
      });

      // Calculate popup position (centered on current window, not screen)
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth / 2) - (width / 2);
      const top = window.screenY + (window.outerHeight / 2) - (height / 2);

      // Build URL to our dedicated Google popup page
      // This page will immediately call signIn('google') when loaded
      const authUrl = `/auth/popup/google?${new URLSearchParams({
        callbackUrl,
      })}`;

      // Open popup window - will load our popup page which triggers Google signin
      const popup = window.open(
        authUrl,
        'Google Sign In',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,menubar=no,scrollbars=yes,status=yes`
      );

      if (!popup) {
        throw new Error('Google Signup popup blocked. Please allow popups for this site in your browser settings.');
      }

      popupRef.current = popup;

      // Poll to detect when popup closes
      pollIntervalRef.current = setInterval(() => {
        try {
          // Check if popup is closed
          // Note: This will throw COOP warnings when popup is at Google's domain (different origin)
          // but it's harmless - the check works when popup returns to our domain
          if (popup.closed) {
            // Popup closed; give NextAuth time to sync session to this window, then decide.
            cancelCheckTimeoutRef.current = setTimeout(() => {
              cancelCheckTimeoutRef.current = null;
              if (isSuccessRef.current) return;
              if (statusRef.current === "authenticated") return;
              setIsAuthenticating(false);
              setError("Sign-in cancelled");
              cleanup();

              trackFunnel("acquisition", "signin_cancelled", {
                auth_method: "google",
                auth_flow: "popup",
              });
            }, 1000);
            cleanup({ preservePostCloseCheck: true });
          }
        } catch (e) {
          // Silently ignore COOP errors when popup is at different origin (Google)
          // The check will work again when popup returns to our domain
        }
      }, 500);

    } catch (err) {
      console.error('[useGooglePopupAuth] Error:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      setIsAuthenticating(false);
      cleanup();

      trackFunnel('acquisition', 'signin_error', {
        auth_method: 'google',
        auth_flow: 'popup',
        error: err instanceof Error ? err.message : 'Unknown error'
      });
    }
  }, [cleanup]);

  const resetAuth = useCallback(() => {
    setIsAuthenticating(false);
    setIsSuccess(false);
    isSuccessRef.current = false;
    setError(null);
    cleanup();
  }, [cleanup]);

  return {
    isAuthenticating,
    isSuccess,
    error,
    initiateGoogleAuth,
    resetAuth,
  };
}

